import { z } from "zod";

/**
 * Shared DTO — ADMIN OVERSIGHT 5 CỖ MÁY CHUYÊN BIỆT (US-E20.F6-0212 · @UC9343–@UC9345):
 * hụi/ROSCA (F1) · sổ hiếu hỉ (F2) · earmark (F3) · đầu tư/NAV (F4) · subscription-slot (F5).
 *
 * BẤT BIẾN (LOAD-BEARING — test khẳng định, không phải lời hứa):
 *  - B1 TIỀN = **STRING BigInt minor** (regex số nguyên). KHÔNG number, KHÔNG float.
 *  - REDACTION 1 NGUỒN: mọi field tiền `.nullable()` — vai ngoài `MONEY_VISIBLE_ROLES`
 *    (SUPPORT/READ_ONLY) nhận **`null`**, KHÔNG phải `0` (0 = nói dối về tiền).
 *  - `.strict()` — field lạ bị reject tại biên (C5).
 *  - Cursor envelope `{ data, pageInfo: { hasNext, nextCursor } }` (keyset createdAt/id).
 *
 * 🔴 HIỂN THỊ KHÔNG ĐƯỢC NÓI DỐI VỀ TIỀN (3 bẫy của 5 cỗ máy — shape ép sự thật):
 *  1. HIẾU HỈ ≠ NỢ: item hiếu hỉ mang `netMinor` + `isSocialObligation: true` + KHÔNG có field
 *     `debtorMemberId`/`dueMinor`/`overdue` nào. Nó là NGHĨA VỤ XÃ HỘI, không đi đòi được.
 *  2. LÃI NAV CHƯA BÁN ≠ TIỀN: `unrealizedPnlMinor` TÁCH KHỎI `fundCashMinor`, cạnh cờ
 *     `unrealizedIsNotCash: true` — không có field nào cộng gộp hai số đó.
 *  3. EARMARKED ≠ TIÊU ĐƯỢC: `availableMinor` = CASH − Σ ràng buộc (KHÔNG phải CASH). `cashMinor`
 *     phơi riêng để admin thấy khoảng cách.
 */

/** Max page-size (mọi list cỗ máy). Default 20 do backend áp (normalizeLimit). */
export const ADMIN_MACHINES_MAX_LIMIT = 100;

/** Tiền minor-units dạng string (BigInt-safe). `null` = REDACTED (vai thấp) — KHÔNG phải 0. */
const moneyString = z
  .string()
  .regex(/^-?\d+$/, "tiền phải là số nguyên minor units (không float)")
  .nullable();

/** Query chung mọi list oversight cỗ máy: lọc theo fund/group + cursor. */
const baseMachineQuery = {
  fundId: z.string().uuid().optional(),
  groupId: z.string().uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_MACHINES_MAX_LIMIT).optional(),
};

const pageInfoSchema = z.object({
  hasNext: z.boolean(),
  nextCursor: z.string().nullable(),
});

/* ══════════════════════ 1) HỤI / ROSCA — bảng dây hụi (F1) ══════════════════════ */

export const adminHuiBoardQuerySchema = z
  .object({
    ...baseMachineQuery,
    /** open | running | completed | broken (1 | CSV) — vắng ⇒ TOÀN BỘ. */
    status: z
      .union([z.string(), z.array(z.string())])
      .transform((v) =>
        (Array.isArray(v) ? v : v.split(",")).map((s) => s.trim()).filter((s) => s.length > 0),
      )
      .pipe(z.array(z.string().min(1).max(32)).min(1))
      .optional(),
  })
  .strict();

export type AdminHuiBoardQuery = z.infer<typeof adminHuiBoardQuerySchema>;

/**
 * 1 dây hụi (board row). `broken` = dây đã vỡ (status='broken'); `outstandingMinor` = Σ nghĩa vụ
 * còn nợ pool của các chân (redact vai thấp). `takenRounds`/`roundCount` = tiến độ (KHÔNG tiền ⇒
 * vai thấp VẪN giám sát được cấu trúc).
 */
export const adminHuiLineItemSchema = z
  .object({
    id: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable(),
    groupId: z.string(),
    groupName: z.string().nullable(),
    name: z.string(),
    kind: z.string(),
    status: z.string(),
    broken: z.boolean(),
    brokenReason: z.string().nullable(),
    ownerMemberId: z.string(),
    ownerName: z.string().nullable(),
    faceValueMinor: moneyString,
    commissionMinor: moneyString,
    outstandingMinor: moneyString,
    roundCount: z.number().int(),
    takenRounds: z.number().int(),
    memberCount: z.number().int(),
    currency: z.string(),
    createdAt: z.string(),
  })
  .strict();

export type AdminHuiLineItem = z.infer<typeof adminHuiLineItemSchema>;

export const adminHuiBoardResponseSchema = z.object({
  data: z.array(adminHuiLineItemSchema),
  pageInfo: pageInfoSchema,
});

export type AdminHuiBoardResponse = z.infer<typeof adminHuiBoardResponseSchema>;

/* ══════════════════ 2) SỔ HIẾU HỈ — net theo counterparty (F2) ══════════════════ */

export const adminReciprocalLogQuerySchema = z
  .object({ ...baseMachineQuery })
  .strict();

export type AdminReciprocalLogQuery = z.infer<typeof adminReciprocalLogQuerySchema>;

/**
 * 🔴 HIẾU HỈ **KHÔNG PHẢI NỢ**. Shape CỐ Ý không có `debtor`/`due`/`overdue`/`settle`:
 *  - `netMinor` = (nhận − đi lễ) — chỉ là SỐ GHI NHỚ ĐẠO LÝ.
 *  - `isSocialObligation: true` — cờ SỰ THẬT, UI phải hiển thị "nghĩa vụ xã hội", không phải "nợ".
 *  - `blocksExit: false` — người còn "nợ đáp lễ" VẪN rời nhóm / xoá tài khoản bình thường.
 */
export const adminReciprocalNetItemSchema = z
  .object({
    id: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable(),
    groupId: z.string().nullable(),
    groupName: z.string().nullable(),
    counterpartyName: z.string(),
    counterpartyMemberId: z.string().nullable(),
    givenMinor: moneyString,
    receivedMinor: moneyString,
    netMinor: moneyString,
    entryCount: z.number().int(),
    currency: z.string(),
    /** 🔴 LUÔN true — hiếu hỉ là nghĩa vụ XÃ HỘI, KHÔNG phải nợ phải đòi. */
    isSocialObligation: z.literal(true),
    /** 🔴 LUÔN false — KHÔNG chặn rời nhóm/xoá tài khoản (không vào financial position). */
    blocksExit: z.literal(false),
    createdAt: z.string(),
  })
  .strict();

export type AdminReciprocalNetItem = z.infer<typeof adminReciprocalNetItemSchema>;

export const adminReciprocalLogResponseSchema = z.object({
  data: z.array(adminReciprocalNetItemSchema),
  pageInfo: pageInfoSchema,
});

export type AdminReciprocalLogResponse = z.infer<typeof adminReciprocalLogResponseSchema>;

/* ═════════════════ 3) EARMARK — mục đích ràng buộc theo quỹ (F3) ═════════════════ */

export const adminEarmarkConfigQuerySchema = z
  .object({
    ...baseMachineQuery,
    /** active | closed. */
    status: z.enum(["active", "closed"]).optional(),
  })
  .strict();

export type AdminEarmarkConfigQuery = z.infer<typeof adminEarmarkConfigQuerySchema>;

/**
 * 1 mục đích ràng buộc. 🔴 `remainingMinor` = tiền GIỮ CHỖ, **KHÔNG tiêu được** cho việc khác.
 * `fundCashMinor` (CASH thật) và `fundAvailableMinor` (= CASH − Σ ràng buộc) phơi RIÊNG ⇒ admin
 * thấy đúng khoảng cách, không nhầm "quỹ còn nhiều tiền".
 */
export const adminEarmarkRuleItemSchema = z
  .object({
    id: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable(),
    groupId: z.string().nullable(),
    groupName: z.string().nullable(),
    purposeTag: z.string(),
    locked: z.boolean(),
    status: z.string(),
    reservedMinor: moneyString,
    disbursedMinor: moneyString,
    remainingMinor: moneyString,
    fundCashMinor: moneyString,
    fundAvailableMinor: moneyString,
    disposition: z.string().nullable(),
    closeReason: z.string().nullable(),
    currency: z.string(),
    createdAt: z.string(),
  })
  .strict();

export type AdminEarmarkRuleItem = z.infer<typeof adminEarmarkRuleItemSchema>;

export const adminEarmarkConfigResponseSchema = z.object({
  data: z.array(adminEarmarkRuleItemSchema),
  pageInfo: pageInfoSchema,
});

export type AdminEarmarkConfigResponse = z.infer<typeof adminEarmarkConfigResponseSchema>;

/* ═══════════════════ 4) ĐẦU TƯ / NAV — vị thế + mốc định giá (F4) ═══════════════════ */

export const adminInvestNavQuerySchema = z
  .object({ ...baseMachineQuery })
  .strict();

export type AdminInvestNavQuery = z.infer<typeof adminInvestNavQuerySchema>;

/**
 * 1 vị thế đầu tư + NAV mới nhất ĐANG HIỆU LỰC (snapshot bị admin từ chối KHÔNG được dùng định giá).
 *
 * 🔴 LÃI CHƯA BÁN ≠ TIỀN: `unrealizedPnlMinor` (VIEW) tách hẳn `fundCashMinor` (tiền THẬT trong sổ);
 * `costBasisMinor` = số dư ASSET_HOLDING (giá vốn). KHÔNG field nào cộng gộp — cờ `unrealizedIsNotCash`
 * là SỰ THẬT bắt buộc UI nói ra.
 */
export const adminInvestPositionItemSchema = z
  .object({
    id: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable(),
    groupId: z.string().nullable(),
    groupName: z.string().nullable(),
    assetSymbol: z.string(),
    status: z.string(),
    totalUnitsScaled: z.string().regex(/^\d+$/),
    costBasisMinor: moneyString,
    navPerUnitMinor: moneyString,
    navAt: z.string().nullable(),
    /** Ai nhập mốc định giá đang dùng (NAV manual dễ gian lận ⇒ luôn phơi). */
    navEnteredBy: z.string().nullable(),
    navSnapshotId: z.string().nullable(),
    marketValueMinor: moneyString,
    unrealizedPnlMinor: moneyString,
    fundCashMinor: moneyString,
    /** Số snapshot admin đã TỪ CHỐI (0 = chưa can thiệp). */
    rejectedNavCount: z.number().int(),
    /** 🔴 LUÔN true — lãi/lỗ chưa bán KHÔNG phải tiền mặt, KHÔNG cộng vào số dư khả dụng. */
    unrealizedIsNotCash: z.literal(true),
    currency: z.string(),
    createdAt: z.string(),
  })
  .strict();

export type AdminInvestPositionItem = z.infer<typeof adminInvestPositionItemSchema>;

export const adminInvestNavResponseSchema = z.object({
  data: z.array(adminInvestPositionItemSchema),
  pageInfo: pageInfoSchema,
});

export type AdminInvestNavResponse = z.infer<typeof adminInvestNavResponseSchema>;

/** Lịch sử mốc định giá 1 vị thế (audit: ai nhập, khi nào, đã bị từ chối chưa). */
export const adminNavSnapshotItemSchema = z
  .object({
    id: z.string(),
    positionId: z.string(),
    navPerUnitMinor: moneyString,
    source: z.string(),
    enteredBy: z.string(),
    enteredAt: z.string(),
    reviewStatus: z.enum(["accepted", "rejected"]),
    reviewedBy: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    reviewNote: z.string().nullable(),
    note: z.string().nullable(),
  })
  .strict();

export type AdminNavSnapshotItem = z.infer<typeof adminNavSnapshotItemSchema>;

export const adminNavHistoryResponseSchema = z.object({
  data: z.array(adminNavSnapshotItemSchema),
  pageInfo: pageInfoSchema,
});

export type AdminNavHistoryResponse = z.infer<typeof adminNavHistoryResponseSchema>;

/* ═════════════ 5) SUBSCRIPTION-SLOT — gói mua chung + ghế (F5) ═════════════ */

export const adminSubscriptionSlotQuerySchema = z
  .object({ ...baseMachineQuery })
  .strict();

export type AdminSubscriptionSlotQuery = z.infer<typeof adminSubscriptionSlotQuerySchema>;

/** 1 ghế của gói + người ĐANG ngồi (vacatedTo NULL). Ghế trống ⇒ occupant null (KHÔNG bịa tên). */
export const adminPoolSlotSchema = z
  .object({
    slotNo: z.number().int(),
    status: z.string(),
    occupantMemberId: z.string().nullable(),
    occupantName: z.string().nullable(),
    occupiedFrom: z.string().nullable(),
  })
  .strict();

export type AdminPoolSlot = z.infer<typeof adminPoolSlotSchema>;

/**
 * 1 gói mua chung. `vacantSlotCount` > 0 ⇒ phí ghế trống được CHIA LẠI cho người đang ngồi
 * (`feePerSlotMinor` = giá NIÊM YẾT khi đầy ghế — KHÔNG phải số thực đóng). Phơi cả hai ⇒ không âm
 * thầm bắt ai gánh mà không biết.
 */
export const adminSubscriptionPoolItemSchema = z
  .object({
    id: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable(),
    groupId: z.string().nullable(),
    groupName: z.string().nullable(),
    serviceName: z.string(),
    status: z.string(),
    totalFeeMinor: moneyString,
    feePerSlotMinor: moneyString,
    slotCount: z.number().int(),
    occupiedSlotCount: z.number().int(),
    vacantSlotCount: z.number().int(),
    ownerMemberId: z.string(),
    ownerName: z.string().nullable(),
    slots: z.array(adminPoolSlotSchema),
    currency: z.string(),
    createdAt: z.string(),
  })
  .strict();

export type AdminSubscriptionPoolItem = z.infer<typeof adminSubscriptionPoolItemSchema>;

export const adminSubscriptionSlotResponseSchema = z.object({
  data: z.array(adminSubscriptionPoolItemSchema),
  pageInfo: pageInfoSchema,
});

export type AdminSubscriptionSlotResponse = z.infer<typeof adminSubscriptionSlotResponseSchema>;

/* ═══════════════════ MUTATION — CAN THIỆP (TÁCH KHỎI READ) 🔴 ═══════════════════ */

/**
 * 🔴 MỌI can thiệp BẮT BUỘC `reason` (≥3 ký tự) — dấu vết ai/vì-sao (@Audited ghi diff).
 * Service admin KHÔNG post/reverse ledger: mỗi mutation GỌI service cỗ máy (F1-F5) — 1 đường sửa.
 */
const reasonField = z.string().trim().min(3, "reason bắt buộc (≥3 ký tự)").max(500);

/** Chốt vỡ hụi (gọi `HuiService.breakHui`: chia thiệt hại peer-debt append-only, KHÔNG xoá nợ). */
export const adminBreakHuiSchema = z.object({ reason: reasonField }).strict();
export type AdminBreakHuiBody = z.infer<typeof adminBreakHuiSchema>;

export const adminBreakHuiResponseSchema = z
  .object({
    huiLineId: z.string(),
    status: z.literal("broken"),
    totalOutstandingMinor: moneyString,
    lossDebtCount: z.number().int(),
  })
  .strict();

export type AdminBreakHuiResponse = z.infer<typeof adminBreakHuiResponseSchema>;

/**
 * KHOÁ / MỞ KHOÁ mục đích earmark (gọi `EarmarkService.setLock`).
 * 🔴 Cờ có CONSUMER THẬT: `locked=false` ⇒ `checkEarmarkedSpend` từ chối chi theo tag (422) —
 * bật/tắt ĐỔI HÀNH VI, không phải toggle trang trí.
 */
export const adminEarmarkLockSchema = z
  .object({ locked: z.boolean(), reason: reasonField })
  .strict();

export type AdminEarmarkLockBody = z.infer<typeof adminEarmarkLockSchema>;

export const adminEarmarkLockResponseSchema = z
  .object({
    ruleId: z.string(),
    fundId: z.string(),
    purposeTag: z.string(),
    locked: z.boolean(),
    status: z.string(),
    remainingMinor: moneyString,
  })
  .strict();

export type AdminEarmarkLockResponse = z.infer<typeof adminEarmarkLockResponseSchema>;

/**
 * DUYỆT / TỪ CHỐI mốc định giá NAV (gọi `InvestService.reviewNav`).
 * 🔴 Cờ có CONSUMER THẬT: snapshot `rejected` KHÔNG được dùng để định giá (contribute/withdraw/view
 * rơi về mốc hợp lệ trước đó) ⇒ NAV thổi giá không bơm được đơn vị/lãi ảo.
 * 🔴 KHÔNG SINH BÚT TOÁN: từ chối NAV = sửa BẢNG GIÁ, không phải sổ cái (ADR 0006).
 */
export const adminReviewNavSchema = z
  .object({ reviewStatus: z.enum(["accepted", "rejected"]), reason: reasonField })
  .strict();

export type AdminReviewNavBody = z.infer<typeof adminReviewNavSchema>;

export const adminReviewNavResponseSchema = z
  .object({
    snapshotId: z.string(),
    positionId: z.string(),
    reviewStatus: z.enum(["accepted", "rejected"]),
    /** NAV ĐANG hiệu lực SAU can thiệp (null = không còn mốc nào hợp lệ ⇒ định giá bị từ chối). */
    effectiveNavPerUnitMinor: moneyString,
    ledgerTxCount: z.literal(0),
  })
  .strict();

export type AdminReviewNavResponse = z.infer<typeof adminReviewNavResponseSchema>;

/**
 * ĐỔI NGƯỜI NGỒI GHẾ (gọi `SubscriptionPoolService.reclaimSlot` + `assignSlot`) — người cũ rời từ
 * `vacatedTo` (inclusive), người mới vào từ `occupiedFrom`. KHÔNG xoá occupancy cũ (dấu vết + kỳ cũ
 * vẫn tính đúng số ngày ⇒ không ai mất tiền, không ai bị tẩy nợ).
 */
export const adminReassignSlotSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    /** YYYY-MM-DD — ngày CUỐI người cũ còn ngồi (inclusive). */
    vacatedTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** YYYY-MM-DD — ngày người mới bắt đầu ngồi. */
    occupiedFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    reason: reasonField,
  })
  .strict();

export type AdminReassignSlotBody = z.infer<typeof adminReassignSlotSchema>;

export const adminReassignSlotResponseSchema = z
  .object({
    poolId: z.string(),
    slotNo: z.number().int(),
    occupantMemberId: z.string(),
    occupiedFrom: z.string(),
  })
  .strict();

export type AdminReassignSlotResponse = z.infer<typeof adminReassignSlotResponseSchema>;
